// Parsing helpers for spreadsheet-style paste into an EntityBulkGridDialog

// Tab-separated text (Excel copy) -> array of string arrays; trailing blank lines dropped
export const parseTsv = (text) =>
  text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line, index, lines) => !(index === lines.length - 1 && line.trim() === ''))
    .map((line) => line.split('\t').map((cell) => cell.trim()));

const normalizeHeader = (header) =>
  String(header ?? '')
    .trim()
    .toLowerCase();

/**
 * Validates the pasted header row against the dialog's columns. Headers must match the template names exactly
 * (case-insensitive); anything unknown or any missing required column rejects the whole paste.
 * Returns { fields } (pasted column index -> field) or { error }.
 */
export const mapHeaders = (headerCells, columns) => {
  const headerToColumn = Object.fromEntries(columns.map((column) => [normalizeHeader(column.header), column]));
  const fields = [];
  const unknown = [];

  headerCells.forEach((cell) => {
    const column = headerToColumn[normalizeHeader(cell)];
    if (!column) {
      if (cell !== '') unknown.push(cell);
      fields.push(null);
    } else if (column.fixed) {
      // Pinned by the dialog (e.g. the owning Program Product); a pasted value is ignored
      fields.push(null);
    } else {
      fields.push(column.field);
    }
  });

  const seen = new Set(fields.filter(Boolean));
  const missing = columns.filter((column) => column.required && !column.fixed && !seen.has(column.field));

  if (seen.size === 0 && unknown.length > 0) {
    return { error: 'The first pasted row must be a header row. Use "Copy headers" to get the expected names.' };
  }
  if (unknown.length > 0 || missing.length > 0) {
    const parts = [];
    if (unknown.length > 0) parts.push('Unknown columns: ' + unknown.map((h) => `"${h}"`).join(', '));
    if (missing.length > 0) parts.push('Missing required columns: ' + missing.map((c) => c.header).join(', '));
    return { error: parts.join('. ') };
  }
  return { fields };
};

// "12%" -> 0.12 ; bare numbers are taken as already-decimal (Excel convention). Returns { value, warning?, error? }
export const parsePercent = (raw) => {
  if (raw == null || raw === '') return { value: null };
  if (typeof raw === 'number') return finishPercent(raw, false);
  const text = String(raw).trim();
  const isPct = text.endsWith('%');
  const num = Number(isPct ? text.slice(0, -1).trim() : text);
  if (!Number.isFinite(num)) return { value: raw, error: `Not a number: ${text}` };
  return finishPercent(isPct ? num / 100 : num, isPct);
};

const finishPercent = (value, explicitPct) => {
  if (!explicitPct && Math.abs(value) > 1) {
    return { value, warning: `Read as ${value * 100}%. Did you mean ${value}% (${value / 100})?` };
  }
  return { value };
};

export const formatPercent = (value) => {
  if (value == null || value === '') return '';
  const num = Number(value);
  return Number.isFinite(num) ? `${Math.round(num * 10000) / 100}%` : String(value);
};

export const parseNumber = (raw) => {
  if (raw == null || raw === '') return { value: null };
  const num = Number(String(raw).replace(/,/g, '').trim());
  return Number.isFinite(num) ? { value: num } : { value: raw, error: `Not a number: ${raw}` };
};

// Accepts yyyymmdd, yyyy-mm-dd, m/d/yyyy -> yyyymmdd integer
export const parseDateInt = (raw) => {
  if (raw == null || raw === '') return { value: null };
  const text = String(raw).trim();
  let match;
  if (/^\d{8}$/.test(text)) return { value: Number(text) };
  if ((match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) return build(match[1], match[2], match[3], text);
  if ((match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) return build(match[3], match[1], match[2], text);
  return { value: raw, error: `Unrecognized date: ${text}` };
};

const build = (year, month, day, text) => {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
    return { value: text, error: `Invalid date: ${text}` };
  }
  return { value: y * 10000 + m * 100 + d };
};

export const formatDateInt = (value) => {
  if (value == null || value === '') return '';
  const text = String(value);
  if (!/^\d{8}$/.test(text)) return text;
  return `${text.slice(4, 6)}/${text.slice(6, 8)}/${text.slice(0, 4)}`;
};

export const parseBoolean = (raw) => {
  if (raw == null || raw === '') return { value: false };
  if (typeof raw === 'boolean') return { value: raw };
  const text = String(raw).trim().toLowerCase();
  if (['y', 'yes', 'true', '1', 'x'].includes(text)) return { value: true };
  if (['n', 'no', 'false', '0'].includes(text)) return { value: false };
  return { value: raw, error: `Expected Y/N: ${raw}` };
};

// Normalizes one raw cell according to its column type. Returns { value, error?, warning? }
export const normalizeCell = (column, raw) => {
  switch (column.type) {
    case 'percent':
      return parsePercent(raw);
    case 'number':
      return parseNumber(raw);
    case 'date':
      return parseDateInt(raw);
    case 'boolean':
      return parseBoolean(raw);
    case 'enum': {
      if (raw == null || raw === '') return { value: null };
      const text = String(raw).trim();
      const match = (column.values ?? []).find(
        (option) =>
          option.code.toLowerCase() === text.toLowerCase() ||
          (option.displayName ?? '').toLowerCase() === text.toLowerCase(),
      );
      return match ? { value: match.code } : { value: raw, error: `Invalid value: ${text}` };
    }
    default:
      return { value: raw == null ? null : String(raw).trim() || null };
  }
};
